/* eslint-disable no-underscore-dangle, jsx-a11y/label-has-associated-control, jsx-a11y/control-has-associated-label, max-len, no-alert */
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
  getAdminImages, getDailyPuzzles, scheduleDailyPuzzle, deleteDailyPuzzle, getPuzzleRatings,
} from '../../services/adminApi';
import './admin.css';

function DailyPuzzleManager() {
  const [images, setImages] = useState([]);
  const [dailyPuzzles, setDailyPuzzles] = useState([]);
  const [puzzleRatings, setPuzzleRatings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleData, setScheduleData] = useState({
    date: new Date().toISOString().split('T')[0],
    adminImageId: '',
    rows: 4,
    columns: 4,
    pieceStyle: 'classic',
  });
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [imagesData, puzzlesData] = await Promise.all([
        getAdminImages(),
        getDailyPuzzles(),
      ]);
      setImages(imagesData.filter((img) => img.isActive));
      setDailyPuzzles(puzzlesData);

      // Fetch ratings for all puzzles (future puzzles will have no ratings, which is fine)
      const ratingsPromises = puzzlesData.map(async (puzzle) => {
        try {
          const ratings = await getPuzzleRatings(puzzle._id);
          return { puzzleId: puzzle._id, ratings };
        } catch {
          return { puzzleId: puzzle._id, ratings: null };
        }
      });

      const ratingsResults = await Promise.all(ratingsPromises);
      const ratingsMap = {};
      ratingsResults.forEach(({ puzzleId, ratings }) => {
        if (ratings) {
          ratingsMap[puzzleId] = ratings;
        }
      });
      setPuzzleRatings(ratingsMap);

      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSchedule = async () => {
    if (!scheduleData.date || !scheduleData.adminImageId) {
      setError('Date and image are required');
      return;
    }

    try {
      setIsScheduling(true);
      await scheduleDailyPuzzle(scheduleData.date, scheduleData.adminImageId, {
        rows: scheduleData.rows,
        columns: scheduleData.columns,
        pieceStyle: scheduleData.pieceStyle,
      });
      setShowScheduleModal(false);
      setSelectedCalendarDate(null);
      setScheduleData({
        date: new Date().toISOString().split('T')[0],
        adminImageId: '',
        rows: 4,
        columns: 4,
        pieceStyle: 'classic',
      });
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsScheduling(false);
    }
  };

  const handleDelete = async (puzzleId) => {
    if (!window.confirm('Are you sure you want to delete this scheduled puzzle?')) return;

    try {
      await deleteDailyPuzzle(puzzleId);
      setDailyPuzzles((prev) => prev.filter((p) => p._id !== puzzleId));
    } catch (err) {
      setError(err.message);
    }
  };

  const isToday = (dateStr) => {
    const today = new Date();
    const date = new Date(dateStr);
    // Compare using UTC to avoid timezone shift issues
    return (
      today.getFullYear() === date.getUTCFullYear()
      && today.getMonth() === date.getUTCMonth()
      && today.getDate() === date.getUTCDate()
    );
  };

  // Create a Set of scheduled date keys for O(1) lookup in calendar
  // Use UTC methods to avoid timezone shift issues
  const scheduledDatesSet = useMemo(() => {
    const dateSet = new Set();
    dailyPuzzles.forEach((puzzle) => {
      const date = new Date(puzzle.date);
      const key = `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`;
      dateSet.add(key);
    });
    return dateSet;
  }, [dailyPuzzles]);

  // Get tile class name for calendar - marks scheduled dates
  const getTileClassName = ({ date, view }) => {
    if (view !== 'month') return null;
    const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
    return scheduledDatesSet.has(key) ? 'react-calendar__tile--scheduled' : null;
  };

  // Get upcoming puzzles (today and future)
  const upcomingPuzzles = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dailyPuzzles
      .filter((puzzle) => new Date(puzzle.date) >= today)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 7);
  }, [dailyPuzzles]);

  // Handle calendar date click
  const handleCalendarDateClick = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Don't allow clicking past dates
    if (date < today) return;

    setSelectedCalendarDate(date);
    setScheduleData((prev) => ({
      ...prev,
      date: date.toISOString().split('T')[0],
    }));
    setShowScheduleModal(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-puzzle-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-puzzle-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-puzzle-background py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-puzzle-text">Daily Puzzle Manager</h1>
            <p className="text-puzzle-text-secondary mt-1">Schedule and manage daily puzzles</p>
          </div>
          <div className="flex gap-4">
            <Link
              to="/admin/stats"
              className="px-4 py-2 bg-puzzle-muted text-puzzle-text rounded-lg hover:bg-puzzle-hover transition-colors"
            >
              Analytics
            </Link>
            <Link
              to="/admin/images"
              className="px-4 py-2 bg-puzzle-muted text-puzzle-text rounded-lg hover:bg-puzzle-hover transition-colors"
            >
              Image Library
            </Link>
            <Link
              to="/admin/feedback"
              className="px-4 py-2 bg-puzzle-muted text-puzzle-text rounded-lg hover:bg-puzzle-hover transition-colors"
            >
              Feedback
            </Link>
            <button
              type="button"
              onClick={() => setShowScheduleModal(true)}
              disabled={images.length === 0}
              className="px-4 py-2 bg-puzzle-blue text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Schedule Puzzle
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 p-4 bg-puzzle-danger/10 border border-puzzle-danger/30 rounded-lg text-puzzle-danger">
            {error}
            <button
              type="button"
              onClick={() => setError(null)}
              className="ml-4 text-puzzle-danger hover:opacity-80"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* No Images Warning */}
        {images.length === 0 && (
          <div className="mb-6 p-4 bg-puzzle-warning/10 border border-puzzle-warning/30 rounded-lg text-puzzle-warning">
            No active images available.
            {' '}
            <Link to="/admin/images" className="underline font-medium">
              Upload images first
            </Link>
            {' '}
            to schedule daily puzzles.
          </div>
        )}

        {/* Calendar and Upcoming Puzzles Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2 bg-puzzle-surface rounded-lg border border-puzzle-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-puzzle-text">Schedule Calendar</h2>
              <div className="flex items-center gap-4 text-sm text-puzzle-text-secondary">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-puzzle-success rounded-full" />
                  Scheduled
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 bg-puzzle-blue rounded-full" />
                  Today
                </span>
              </div>
            </div>
            <p className="text-sm text-puzzle-text-secondary mb-4">
              Click on any future date to schedule a puzzle for that day.
            </p>
            <Calendar
              onClickDay={handleCalendarDateClick}
              value={selectedCalendarDate}
              minDate={new Date()}
              tileClassName={getTileClassName}
              className="w-full border-0"
            />
          </div>

          {/* Upcoming Puzzles Sidebar */}
          <div className="bg-puzzle-surface rounded-lg border border-puzzle-border p-6">
            <h2 className="text-lg font-semibold text-puzzle-text mb-4">Upcoming Puzzles</h2>
            {upcomingPuzzles.length === 0 ? (
              <div className="text-center py-8">
                <svg className="mx-auto h-10 w-10 text-puzzle-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="mt-2 text-sm text-puzzle-text-secondary">No upcoming puzzles</p>
                <p className="text-xs text-puzzle-text-secondary">Click a date to schedule</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingPuzzles.map((puzzle) => (
                  <div
                    key={puzzle._id}
                    className={`p-3 rounded-lg border ${
                      isToday(puzzle.date) ? 'border-puzzle-success bg-puzzle-success/10' : 'border-puzzle-border bg-puzzle-muted'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Thumbnail */}
                      <div className="w-12 h-12 flex-shrink-0 rounded overflow-hidden bg-puzzle-muted">
                        {puzzle.adminImageId?.thumbnailData && (
                          <img
                            src={puzzle.adminImageId.thumbnailData}
                            alt={puzzle.adminImageId.title}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <p className="text-sm font-medium text-puzzle-text truncate">
                            {new Date(puzzle.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}
                          </p>
                          {isToday(puzzle.date) && (
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-puzzle-success/20 text-puzzle-success rounded">
                              Today
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-puzzle-text-secondary truncate">
                          {puzzle.adminImageId?.title || 'Unknown'}
                        </p>
                        <p className="text-xs text-puzzle-text-secondary">
                          {puzzle.config.rows}
                          x
                          {puzzle.config.columns}
                          {' '}
                          {puzzle.config.pieceStyle}
                        </p>
                        {/* Ratings display */}
                        {puzzleRatings[puzzle._id] && puzzleRatings[puzzle._id].total > 0 && (
                          <p className="text-xs text-puzzle-text-secondary mt-1">
                            <span title="Loved it">
                              {'😊 '}
                              {puzzleRatings[puzzle._id].happy}
                            </span>
                            {' · '}
                            <span title="It was okay">
                              {'😐 '}
                              {puzzleRatings[puzzle._id].neutral}
                            </span>
                            {' · '}
                            <span title="Not great">
                              {'😞 '}
                              {puzzleRatings[puzzle._id].sad}
                            </span>
                          </p>
                        )}
                      </div>
                      {/* Delete */}
                      <button
                        type="button"
                        onClick={() => handleDelete(puzzle._id)}
                        className="p-1 text-puzzle-text-secondary hover:text-puzzle-danger hover:bg-puzzle-danger/10 rounded transition-colors"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Schedule Modal */}
        {showScheduleModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-puzzle-surface rounded-lg max-w-lg w-full p-6">
              <h2 className="text-xl font-bold mb-4">Schedule Daily Puzzle</h2>

              <div className="space-y-4">
                {/* Date */}
                <div>
                  <label htmlFor="date" className="block text-sm font-medium text-puzzle-text mb-1">
                    Date *
                  </label>
                  <input
                    type="date"
                    id="date"
                    value={scheduleData.date}
                    onChange={(e) => setScheduleData((prev) => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-puzzle-blue focus:border-puzzle-blue bg-puzzle-surface text-puzzle-text"
                  />
                </div>

                {/* Image Selection */}
                <div>
                  <label htmlFor="image" className="block text-sm font-medium text-puzzle-text mb-1">
                    Image *
                  </label>
                  <select
                    id="image"
                    value={scheduleData.adminImageId}
                    onChange={(e) => setScheduleData((prev) => ({ ...prev, adminImageId: e.target.value }))}
                    className="w-full px-3 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-puzzle-blue focus:border-puzzle-blue bg-puzzle-surface text-puzzle-text"
                  >
                    <option value="">Select an image...</option>
                    {images.map((img) => (
                      <option key={img._id} value={img._id}>
                        {img.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Grid Size */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="rows" className="block text-sm font-medium text-puzzle-text mb-1">
                      Rows
                    </label>
                    <select
                      id="rows"
                      value={scheduleData.rows}
                      onChange={(e) => setScheduleData((prev) => ({ ...prev, rows: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-puzzle-blue focus:border-puzzle-blue bg-puzzle-surface text-puzzle-text"
                    >
                      {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="columns" className="block text-sm font-medium text-puzzle-text mb-1">
                      Columns
                    </label>
                    <select
                      id="columns"
                      value={scheduleData.columns}
                      onChange={(e) => setScheduleData((prev) => ({ ...prev, columns: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-puzzle-blue focus:border-puzzle-blue bg-puzzle-surface text-puzzle-text"
                    >
                      {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Piece Style */}
                <div>
                  <label htmlFor="pieceStyle" className="block text-sm font-medium text-puzzle-text mb-1">
                    Piece Style
                  </label>
                  <select
                    id="pieceStyle"
                    value={scheduleData.pieceStyle}
                    onChange={(e) => setScheduleData((prev) => ({ ...prev, pieceStyle: e.target.value }))}
                    className="w-full px-3 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-puzzle-blue focus:border-puzzle-blue bg-puzzle-surface text-puzzle-text"
                  >
                    <option value="classic">Classic</option>
                    <option value="traditional">Traditional</option>
                    <option value="rounded">Rounded</option>
                    <option value="wavy">Wavy</option>
                    <option value="square">Square</option>
                    <option value="random">Random</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowScheduleModal(false);
                    setSelectedCalendarDate(null);
                  }}
                  className="px-4 py-2 text-puzzle-text hover:bg-puzzle-hover rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSchedule}
                  disabled={isScheduling || !scheduleData.date || !scheduleData.adminImageId}
                  className="px-4 py-2 bg-puzzle-blue text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isScheduling ? 'Scheduling...' : 'Schedule'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DailyPuzzleManager;
